/**
 * Shared bank-activation condition checks.
 * Land and construction facilities have separate condition sets.
 */

import { n } from '../math.js';

/**
 * Construction credit: permit + presale threshold + sponsor equity + facility + costs/headroom.
 * @returns {{ met: boolean, unmet: string[], headroom: number }}
 */
export function evaluateConstructionBankConditions({
  month,
  programme,
  salesPct,
  threshold,
  sponsorEquityInjected,
  requiredSponsorEquity,
  facilitySecured,
  eligibleCostsIncurred,
  drawnToDate,
  requestedDraw,
}) {
  const unmet = [];

  if (month < programme.permitMonth) {
    unmet.push('Permit / authorization not yet obtained');
  }
  if (salesPct + 1e-9 < threshold) {
    unmet.push(
      `Presale threshold not met (${(salesPct * 100).toFixed(0)}% sold; need ${(threshold * 100).toFixed(0)}%)`,
    );
  }
  if (sponsorEquityInjected + 1e-6 < requiredSponsorEquity) {
    unmet.push('Required sponsor equity not yet injected');
  }
  if (facilitySecured <= 1e-6) {
    unmet.push('No secured bank facility entered');
  }
  if (eligibleCostsIncurred <= 1e-6 && requestedDraw > 1e-6) {
    unmet.push('No eligible costs incurred for a bank draw');
  }
  const headroom = Math.max(0, facilitySecured - drawnToDate);
  if (requestedDraw > 1e-6 && headroom <= 1e-6) {
    unmet.push('Facility commitment fully drawn');
  }

  return { met: unmet.length === 0, unmet, headroom };
}

/**
 * Land / acquisition facility: drawable at land-acquisition month under its own
 * conditions (optional rental self-sufficiency). Does NOT require permit or
 * construction presales.
 */
export function evaluateLandBankConditions({
  month,
  programme,
  facilitySecured,
  eligibleCostsIncurred,
  drawnToDate,
  requestedDraw,
  landRequiresRentalSelfSufficiency = false,
  rentalSelfSufficient = true,
}) {
  const unmet = [];

  if (month < programme.acquisitionMonth) {
    unmet.push('Land acquisition stage not yet reached');
  }
  if (landRequiresRentalSelfSufficiency && !rentalSelfSufficient) {
    unmet.push('Rental self-sufficiency not met for land facility');
  }
  if (facilitySecured <= 1e-6) {
    unmet.push('No secured land facility entered');
  }
  if (eligibleCostsIncurred <= 1e-6 && requestedDraw > 1e-6) {
    unmet.push('No eligible land costs incurred for a bank draw');
  }
  const headroom = Math.max(0, facilitySecured - drawnToDate);
  if (requestedDraw > 1e-6 && headroom <= 1e-6) {
    unmet.push('Land facility commitment fully drawn');
  }

  return { met: unmet.length === 0, unmet, headroom };
}

/** @deprecated Prefer evaluateConstructionBankConditions for construction draws. */
export function evaluateBankConditions(args) {
  return evaluateConstructionBankConditions(args);
}

export function constructionPctAtMonth(month, programme, costSchedule) {
  const build = costSchedule.byCategory.constructionCost || [];
  const reserve = costSchedule.byCategory.constructionReserve || [];
  const total = build.reduce((s, v) => s + n(v), 0) + reserve.reduce((s, v) => s + n(v), 0);
  if (total <= 0) return month >= programme.constructionEnd ? 1 : 0;
  let cum = 0;
  for (let i = 0; i < month && i < build.length; i += 1) {
    cum += n(build[i]) + n(reserve[i]);
  }
  return Math.min(1, cum / total);
}
